import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2, MapPin, RefreshCw } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/api/client';
import TrackingBadge from '@/components/mail/TrackingBadge';
import { describeTrackingStatus } from '@/lib/tracking-status';

const SHOP_TIME_ZONE = 'America/New_York';

const when = (value) => formatInTimeZone(new Date(value), SHOP_TIME_ZONE, 'MMM d, h:mm a');

/**
 * Where one label is, and how it got there.
 *
 * Updates arrive by themselves - pushed by the carrier's service when it can
 * be, polled every half hour when it cannot - so this mostly just reads. The
 * Refresh button is for the person at the counter with a customer waiting, who
 * should not have to wait for the timer.
 */
export default function TrackingDialog({ label, onClose }) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['labelTracking', label.id],
    queryFn: () => api.request(`/tracking/labels/${label.id}`),
  });

  const refresh = useMutation({
    mutationFn: () => api.request(`/tracking/labels/${label.id}/refresh`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelTracking', label.id] });
      queryClient.invalidateQueries({ queryKey: ['mailLabels'] });
    },
    onError: (err) => toast.error(err.message ?? 'Could not check with the carrier'),
  });

  const events = data?.trackingEvents ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Tracking — {label.recipientName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-mono text-sm">{label.trackingNumber ?? 'No tracking number'}</p>
              <p className="text-xs text-slate-500">
                {label.carrier.toUpperCase()} — {label.serviceName}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <TrackingBadge status={data?.trackingStatus ?? label.trackingStatus} />
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={refresh.isPending}
                onClick={() => refresh.mutate()}
              >
                {refresh.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Refresh
              </Button>
            </div>
          </div>

          {(data?.deliveredAt ?? label.deliveredAt) && (
            <p className="text-sm text-green-700">
              Delivered {when(data?.deliveredAt ?? label.deliveredAt)}
            </p>
          )}

          {data?.trackingPublicUrl && (
            <a
              href={data.trackingPublicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Customer tracking page
              <ExternalLink className="w-3 h-3" />
            </a>
          )}

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : isError ? (
            <p className="text-sm text-red-600">{error.message ?? 'Could not load tracking'}</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-slate-500">
              No scans yet. The carrier usually records the first one when the parcel is handed over.
              Try Refresh to ask them now.
            </p>
          ) : (
            <ol className="space-y-3 border-l-2 border-slate-200 pl-4">
              {events.map((event) => (
                <li key={event.id} className="relative">
                  <span className="absolute -left-[1.4rem] top-1.5 w-2 h-2 rounded-full bg-slate-400" />
                  <p className="text-sm font-medium text-slate-900">
                    {describeTrackingStatus(event.status).label}
                  </p>
                  {event.statusDetails && (
                    <p className="text-sm text-slate-600">{event.statusDetails}</p>
                  )}
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    {when(event.occurredAt)}
                    {event.location && (
                      <>
                        <MapPin className="w-3 h-3 ml-1" />
                        {event.location}
                      </>
                    )}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
